import type { EngagementLevel, SurfacedItem } from '@app/core';
import { create } from 'zustand';

import type { ItemService } from '../services/item-service.js';
import type { InteractionRepository } from '../services/repositories.js';
import type { TimeProvider } from '../services/time-provider.js';

interface ItemsState {
  queue: SurfacedItem[];
  focusIndex: number;
  loading: boolean;

  loadQueue: (service: ItemService, engagement: EngagementLevel) => Promise<void>;
  complete: (
    service: ItemService,
    interactionRepo: InteractionRepository,
    engagement: EngagementLevel,
    sessionId: string,
    time: TimeProvider,
  ) => Promise<void>;
  snooze: (
    service: ItemService,
    interactionRepo: InteractionRepository,
    engagement: EngagementLevel,
    sessionId: string,
    time: TimeProvider,
  ) => Promise<void>;
  dismiss: (
    service: ItemService,
    interactionRepo: InteractionRepository,
    engagement: EngagementLevel,
    sessionId: string,
    time: TimeProvider,
  ) => Promise<void>;
  reheat: (
    service: ItemService,
    interactionRepo: InteractionRepository,
    engagement: EngagementLevel,
    sessionId: string,
    time: TimeProvider,
  ) => Promise<void>;
  skipTo: (
    index: number,
    service: ItemService,
    interactionRepo: InteractionRepository,
    engagement: EngagementLevel,
    sessionId: string,
    time: TimeProvider,
  ) => Promise<void>;
}

export const useItemsStore = create<ItemsState>((set, get) => ({
  queue: [],
  focusIndex: 0,
  loading: true,

  loadQueue: async (service, engagement) => {
    const queue = await service.getSurfacingQueue(engagement);
    set({ queue, focusIndex: 0, loading: false });
  },

  complete: async (service, interactionRepo, engagement, sessionId, time) => {
    const { queue, focusIndex } = get();
    const focus = queue[focusIndex];
    if (!focus) return;
    await logInteraction(interactionRepo, focus, 'completed', sessionId, engagement, time);
    await service.complete(focus.item.id);
    await get().loadQueue(service, engagement);
  },

  snooze: async (service, interactionRepo, engagement, sessionId, time) => {
    const { queue, focusIndex } = get();
    const focus = queue[focusIndex];
    if (!focus) return;
    await logInteraction(interactionRepo, focus, 'snoozed', sessionId, engagement, time);
    await service.snooze(focus.item.id);
    await get().loadQueue(service, engagement);
  },

  dismiss: async (service, interactionRepo, engagement, sessionId, time) => {
    const { queue, focusIndex } = get();
    const focus = queue[focusIndex];
    if (!focus) return;
    await logInteraction(interactionRepo, focus, 'dismissed', sessionId, engagement, time);
    await service.dismiss(focus.item.id);
    await get().loadQueue(service, engagement);
  },

  reheat: async (service, interactionRepo, engagement, sessionId, time) => {
    const { queue, focusIndex } = get();
    const focus = queue[focusIndex];
    if (!focus) return;
    await logInteraction(interactionRepo, focus, 'reheated', sessionId, engagement, time);
    await service.reheat(focus.item.id);
    await get().loadQueue(service, engagement);
  },

  skipTo: async (index, service, interactionRepo, engagement, sessionId, time) => {
    const { queue, focusIndex } = get();
    const focus = queue[focusIndex];
    const target = queue[index];
    if (!focus || !target) return;
    await logInteraction(interactionRepo, focus, 'skipped', sessionId, engagement, time, target.item.id);
    set({ focusIndex: index });
  },
}));

async function logInteraction(
  repo: InteractionRepository,
  surfaced: SurfacedItem,
  action: 'completed' | 'snoozed' | 'dismissed' | 'skipped' | 'reheated',
  sessionId: string,
  engagement: EngagementLevel,
  time: TimeProvider,
  skipTarget?: string,
): Promise<void> {
  await repo.log({
    itemId: surfaced.item.id,
    sessionId,
    timestamp: time.now(),
    action,
    temperatureAtSurfacing: surfaced.temperature,
    engagementLevel: engagement,
    queuePosition: surfaced.queuePosition,
    skipTarget,
  });
}
